import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { useRequest } from "@prismicio/editor-support/Suspense"
import { z } from "zod"

import {
  type AuthStrategy,
  throwConfigError,
  useEditorConfig,
} from "../../EditorConfig"

const role = z.enum(["Owner", "Administrator", "Writer", "Manager", "Readonly"])
const rolesPerLocale = z.record(role.optional())
const repositoryRole = role.or(rolesPerLocale)

export const roleMap = {
  SuperUser: "SuperUser",
  Owner: "Owner",
  Administrator: "Administrator",
  Manager: "Publisher",
  Writer: "Writer",
  Readonly: "Contributor",
} as const

export type Role = (typeof roleMap)[keyof typeof roleMap]
export type RepositoryRole = z.TypeOf<typeof repositoryRole>

export const profileResponse = z.object({
  shortId: z.string(),
  intercomHash: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  superPower: z.boolean(),
  repositories: z.array(
    z.object({
      domain: z.string(),
      name: z.string().optional(),
      role: repositoryRole,
    }),
  ),
})

export type Profile = z.infer<typeof profileResponse>

export async function getProfile(
  userServiceBaseUrl: URL,
  authStrategy: AuthStrategy,
): Promise<Profile> {
  const url = new URL("profile", userServiceBaseUrl)
  const profile = await request(url, {
    credentials: getRequestCredentials(authStrategy),
    okType: profileResponse,
  })
  return profile
}

export function useProfile() {
  const config = useEditorConfig()

  if (!config.userServiceBaseUrl) throwConfigError("userServiceBaseUrl")

  const { authStrategy, userServiceBaseUrl } = config

  return useRequest(getProfile, [userServiceBaseUrl, authStrategy])
}
